import { Injectable } from '@angular/core';
import readXlsxFile from "read-excel-file";

interface User {
  email: string;
  amount: number | null | undefined;
}

type Users = {
  users: Array<User>;
  totalAmount: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImportService {
  #importedUsers: Array<User> = []

  /**
   * Parses the users from the excel file.
   * Call `getUsers` to get the parsed users.
   * @returns The number of users parsed.
   */
  async importFromExcelFile(file: File): Promise<number> {
    const rows = await readXlsxFile(file)
    this.#importedUsers = rows.map((row) => {
      if (typeof row[0] !== 'string') {
        throw new Error('Invalid email: ' + row[0])
      }

      if (typeof row[1] === "number" && row[1] < 0) {
        throw new Error("Amount can't be negative: " + row[1]);
      }

      if (typeof row[1] !== "number" && typeof row[1] !== "undefined" && row[1] !== null) {
        throw new Error("Invalid amount, expected a number but got: " + row[1]);
      }

      return { email: row[0], amount: row[1] as number | null | undefined };
    });
    return this.#importedUsers.length;
  }

  getEmails(): Array<string> {
    return this.#importedUsers.map((user) => user.email);
  }

  /**
   * @returns The users parsed by a previous invocation of `importFromExcelFile`.
   *          For users where no amount was specified in the excel file,
   *          the amount will be the default amount.
   */
  getUsers(defaultAmount: number): Users {
    let totalAmount = 0
    const users = this.#importedUsers.map((user) => {
      const amount = user.amount ?? defaultAmount;
      totalAmount += amount;
      return {
        ...user,
        amount
      }
    });

    return {
      users,
      totalAmount
    }
  }

  /**
   * Clears the imported users.
   */
  clear() {
    this.#importedUsers = [];
  }
}
